/**
 * Events page. Backend: hoodieApi getEvents (search param). Frontend-only filtering via
 * same left sidebar pattern as M List. Uses mlist layout and sidebar styling.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getEvents, isHoodieApiConfigured } from "../../../api";
import {
  EventsHeroIntroSection,
  EventsFilterBar,
  EventsSidebar,
  EventsResultsSection,
} from "../events";
import { normalizeEvent, applyEventsFilters } from "../events/eventsFilterConfig";
import "../styles/TwentyTwentysixContent.css";

const EVENT_TYPE_CARDS = [
  { title: "Virtual Events", description: "Stay connected with other moms from home. Online meetups, panels, and support circles." },
  { title: "Local Gatherings", description: "Connect with local moms in your area. Park meetups, coffee dates, and chapter events." },
  { title: "Moving Together", description: "Initiatives for yourself, family, and community. Walks, wellness, and social impact." },
];

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(isHoodieApiConfigured());
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [searchApplied, setSearchApplied] = useState("");
  const [attendanceIds, setAttendanceIds] = useState([]);
  const [curatedSeriesIds, setCuratedSeriesIds] = useState([]);
  const [locationIds, setLocationIds] = useState([]);

  const normalizedEvents = useMemo(
    () => (Array.isArray(events) ? events.map(normalizeEvent) : []),
    [events]
  );

  const filterState = useMemo(
    () => ({
      search: searchApplied,
      attendanceIds,
      curatedSeriesIds,
      locationIds,
    }),
    [searchApplied, attendanceIds, curatedSeriesIds, locationIds]
  );

  const filteredEvents = useMemo(
    () => applyEventsFilters(normalizedEvents, filterState),
    [normalizedEvents, filterState]
  );

  const hasActiveFilters =
    searchApplied.trim() !== "" ||
    attendanceIds.length > 0 ||
    curatedSeriesIds.length > 0 ||
    locationIds.length > 0;

  const fetchEvents = useCallback((searchTerm) => {
    if (!isHoodieApiConfigured()) return;
    setLoading(true);
    setError(null);
    const params = typeof searchTerm === "string" && searchTerm.trim() ? { search: searchTerm.trim() } : {};
    getEvents(params)
      .then((data) => {
        setEvents(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        setError(err?.message || "Could not load events");
        setEvents([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!isHoodieApiConfigured()) {
      setLoading(false);
      return;
    }
    fetchEvents(searchApplied);
  }, [searchApplied, fetchEvents]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearchApplied(searchInput);
  };

  const handleClearAll = useCallback(() => {
    setSearchInput("");
    setSearchApplied("");
    setAttendanceIds([]);
    setCuratedSeriesIds([]);
    setLocationIds([]);
  }, []);

  return (
    <div className="twentytwentysix-content">
      <div className="twentytwentysix-content__container">
        <EventsHeroIntroSection />

        {!isHoodieApiConfigured() && (
          <section className="twentytwentysix-content__section twentytwentysix-content__api-notice" role="status">
            <p className="twentytwentysix-content__copy twentytwentysix-content__api-notice-text">
              <strong>No Events API connected.</strong> To see events here, set <code>REACT_APP_HOODIE_API_BASE_URL</code> to your Django backend, then restart the dev server. Ensure the backend is serving <code>/api/hoodie/events/</code>.
            </p>
          </section>
        )}

        {error && (
          <div className="twentytwentysix-content__section" role="alert" style={{ padding: "12px", background: "#fde8e8", borderRadius: "8px", color: "#c00" }}>
            {error}
          </div>
        )}

        {isHoodieApiConfigured() && (
          <div className="twentytwentysix-content__mlist-layout">
            <EventsSidebar
              attendanceIds={attendanceIds}
              onAttendanceIdsChange={setAttendanceIds}
              curatedSeriesIds={curatedSeriesIds}
              onCuratedSeriesIdsChange={setCuratedSeriesIds}
              locationIds={locationIds}
              onLocationIdsChange={setLocationIds}
              onClearAll={handleClearAll}
              hasActiveFilters={hasActiveFilters}
            />

            <main className="twentytwentysix-content__mlist-main">
              <EventsFilterBar
                searchInput={searchInput}
                onSearchChange={setSearchInput}
                onSearchSubmit={handleSearchSubmit}
                onClearAll={handleClearAll}
                hasActiveFilters={hasActiveFilters}
              />

              <EventsResultsSection
                events={filteredEvents}
                loading={loading}
                searchApplied={searchApplied}
                hasActiveFilters={hasActiveFilters}
              />
            </main>
          </div>
        )}

        <section className="twentytwentysix-content__section">
          <h2 className="twentytwentysix-content__subtitle">Event types</h2>
          <div className="twentytwentysix-content__card-grid">
            {EVENT_TYPE_CARDS.map((card) => (
              <div className="twentytwentysix-content__card" key={card.title}>
                <h4>{card.title}</h4>
                <p>{card.description}</p>
              </div>
            ))}
          </div>
          <p className="twentytwentysix-content__copy">
            We have chapters in San Francisco Bay Area, Sacramento Valley, and LA County. Check the calendar for events near you.
          </p>
        </section>
      </div>
    </div>
  );
}
